(function () {
  'use strict';

  var initializedCarousels = new WeakSet();
  var initializedMinis = new WeakSet();
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function closestCardIndex(track, cards) {
    var target = track.scrollLeft;
    var closest = 0;
    var distance = Infinity;

    cards.forEach(function (card, index) {
      var currentDistance = Math.abs(card.offsetLeft - track.offsetLeft - target);
      if (currentDistance < distance) {
        closest = index;
        distance = currentDistance;
      }
    });

    return closest;
  }

  function initCarousel(root) {
    if (!root || initializedCarousels.has(root)) return;

    var track = root.querySelector('[data-da-review-track]');
    var cards = track ? Array.prototype.slice.call(track.querySelectorAll('[data-da-review-card]')) : [];
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-da-review-dot]'));
    var previous = root.querySelector('[data-da-review-prev]');
    var next = root.querySelector('[data-da-review-next]');
    var ticking = false;
    var autoTimer = null;
    var restartTimer = null;
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var startScrollLeft = 0;
    var dragging = false;
    var interval = Number(root.dataset.interval) || 3800;
    var hasHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    var isIntersecting = !('IntersectionObserver' in window);
    var pageScrollTimer = null;

    if (!track || !cards.length) return;
    initializedCarousels.add(root);

    function isMobile() {
      return window.innerWidth < 750;
    }

    function stopAutoplay() {
      if (autoTimer) window.clearTimeout(autoTimer);
      autoTimer = null;
    }

    function scheduleAutoplay(delay) {
      stopAutoplay();
      if (!isMobile() || cards.length < 2 || !isIntersecting || document.hidden || !root.isConnected || root.classList.contains('ddr-hidden')) return;
      autoTimer = window.setTimeout(function () {
        goTo((closestCardIndex(track, cards) + 1) % cards.length);
        scheduleAutoplay(interval);
      }, delay || interval);
    }

    function pauseAutoplay() {
      stopAutoplay();
      if (restartTimer) window.clearTimeout(restartTimer);
      restartTimer = null;
      if (isMobile() && cards.length > 1) {
        restartTimer = window.setTimeout(function () { scheduleAutoplay(interval); }, Math.max(interval, 6500));
      }
    }

    function update(index) {
      cards.forEach(function (card, cardIndex) {
        card.setAttribute('aria-hidden', window.innerWidth < 750 && cardIndex !== index ? 'true' : 'false');
      });
      dots.forEach(function (dot, dotIndex) {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (previous) previous.disabled = cards.length < 2;
      if (next) next.disabled = cards.length < 2;
    }

    function goTo(index) {
      var safeIndex = ((index % cards.length) + cards.length) % cards.length;
      track.scrollTo({
        left: cards[safeIndex].offsetLeft - track.offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      update(safeIndex);
    }

    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update(closestCardIndex(track, cards));
        ticking = false;
      });
    }, { passive: true });

    track.addEventListener('keydown', function (event) {
      var current = closestCardIndex(track, cards);
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        pauseAutoplay();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        pauseAutoplay();
        goTo(current - 1);
      }
    });

    if (previous) previous.addEventListener('click', function () {
      pauseAutoplay();
      goTo(closestCardIndex(track, cards) - 1);
    });
    if (next) next.addEventListener('click', function () {
      pauseAutoplay();
      goTo(closestCardIndex(track, cards) + 1);
    });
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        pauseAutoplay();
        goTo(index);
      });
    });

    track.addEventListener('pointerdown', function (event) {
      if (!isMobile() || (event.pointerType === 'mouse' && event.button !== 0)) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
      dragging = false;
      pauseAutoplay();
    });

    track.addEventListener('pointermove', function (event) {
      if (pointerId !== event.pointerId) return;
      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;

      if (!dragging && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        dragging = true;
        root.classList.add('is-dragging');
        if (track.setPointerCapture) track.setPointerCapture(pointerId);
      } else if (!dragging && Math.abs(deltaY) > 8) {
        pointerId = null;
        return;
      }

      if (dragging) {
        event.preventDefault();
        track.scrollLeft = startScrollLeft - deltaX;
      }
    }, { passive: false });

    function finishDrag(event) {
      if (pointerId !== event.pointerId) return;
      var shouldSnap = dragging;
      if (track.releasePointerCapture && track.hasPointerCapture && track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = null;
      dragging = false;
      root.classList.remove('is-dragging');
      if (shouldSnap) goTo(closestCardIndex(track, cards));
      pauseAutoplay();
    }

    track.addEventListener('pointerup', finishDrag);
    track.addEventListener('pointercancel', finishDrag);

    if (hasHover) {
      root.addEventListener('mouseenter', stopAutoplay);
      root.addEventListener('mouseleave', function () { scheduleAutoplay(interval); });
    }
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', function () { scheduleAutoplay(interval); });
    root.addEventListener('da-review:visibility', function () {
      if (root.classList.contains('ddr-hidden')) stopAutoplay();
      else scheduleAutoplay(interval);
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else scheduleAutoplay(interval);
    });
    window.addEventListener('resize', function () {
      if (isMobile()) scheduleAutoplay(interval);
      else stopAutoplay();
    }, { passive: true });

    window.addEventListener('scroll', function () {
      if (!isMobile()) return;
      stopAutoplay();
      if (pageScrollTimer) window.clearTimeout(pageScrollTimer);
      pageScrollTimer = window.setTimeout(function () { scheduleAutoplay(interval); }, 900);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      var visibilityObserver = new IntersectionObserver(function (entries) {
        isIntersecting = entries.some(function (entry) { return entry.isIntersecting && entry.intersectionRatio >= 0.35; });
        if (isIntersecting) scheduleAutoplay(interval);
        else stopAutoplay();
      }, { threshold: [0, 0.35, 0.7] });
      visibilityObserver.observe(root);
    }

    update(0);
    scheduleAutoplay(interval);
  }

  function initMini(root) {
    if (!root || initializedMinis.has(root)) return;

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-da-mini-item]')).map(function (item) {
      return { quote: item.dataset.quote || '', author: item.dataset.author || '' };
    }).filter(function (item) { return item.quote; });
    var quote = root.querySelector('[data-da-mini-quote]');
    var author = root.querySelector('[data-da-mini-author]');
    var index = 0;
    var timer = null;
    var interval = Number(root.dataset.interval) || 4200;

    if (!quote || !author || !items.length) return;
    initializedMinis.add(root);

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    function renderNext() {
      if (document.hidden || !root.isConnected) {
        if (!root.isConnected) stop();
        return;
      }
      if (reduceMotion) {
        index = (index + 1) % items.length;
        quote.textContent = items[index].quote;
        author.textContent = items[index].author + ' ✓';
        return;
      }
      root.classList.add('is-fading');
      window.setTimeout(function () {
        index = (index + 1) % items.length;
        quote.textContent = items[index].quote;
        author.textContent = items[index].author + ' ✓';
        root.classList.remove('is-fading');
      }, 220);
    }

    function start() {
      stop();
      if (items.length > 1) timer = window.setInterval(renderNext, interval);
    }

    if (window.matchMedia && window.matchMedia('(hover: hover)').matches && window.innerWidth >= 750) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
    }
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    start();
  }

  function syncOfferReview(reviewSection) {
    if (!reviewSection) return;
    var gate = reviewSection.nextElementSibling;
    while (gate && !gate.classList.contains('ddr-gate')) gate = gate.nextElementSibling;
    var hidden = !gate || gate.classList.contains('ddr-hidden');
    reviewSection.classList.toggle('ddr-hidden', hidden);
    reviewSection.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    reviewSection.dispatchEvent(new CustomEvent('da-review:visibility'));
  }

  function initialize(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    if (scope && scope.matches) {
      if (scope.matches('[data-da-carousel]')) initCarousel(scope);
      if (scope.matches('[data-da-mini]')) initMini(scope);
      if (scope.matches('[data-da-review-sync]')) syncOfferReview(scope);
    }
    root.querySelectorAll('[data-da-carousel]').forEach(initCarousel);
    root.querySelectorAll('[data-da-mini]').forEach(initMini);
    root.querySelectorAll('[data-da-review-sync]').forEach(syncOfferReview);
  }

  function syncGate(gate) {
    var reviewSection = gate && gate.previousElementSibling;
    if (reviewSection && reviewSection.matches('[data-da-review-sync]')) syncOfferReview(reviewSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initialize(document); });
  else initialize(document);

  var observer = new MutationObserver(function (records) {
    records.forEach(function (record) {
      if (record.type === 'attributes' && record.target.classList.contains('ddr-gate')) syncGate(record.target);
      record.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) initialize(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-da-review-track]').forEach(function (track) {
      track.dispatchEvent(new Event('scroll'));
    });
  }, { passive: true });
})();
